import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomButton from './CustomButton';
import CpfMask from './InputMask/CpfMask';
import PhoneMask from './InputMask/PhoneMask';
import { useRegister } from '../context/RegisterContext';
import { ResponsavelData } from '../types/RegisterType';
import { saveLeadFromCadastro } from '../api/lead';
import './StepResponsavel.css';

interface Props {
  onNext: () => void;
  onBack?: () => void;
}

const schema = yup.object({
  nomeCompleto: yup.string().required('Campo obrigatório'),
  cpf: yup
    .string()
    .required('Campo obrigatório')
    .test('cpf', 'CPF inválido', val => (val?.replace(/\D/g, '') ?? '').length === 11),
  telefone: yup
    .string()
    .required('Campo obrigatório')
    .test('telefone', 'Telefone inválido', val => {
      const digits = val?.replace(/\D/g, '') ?? '';
      return digits.length >= 10 && digits.length <= 11;
    }),
  email: yup.string().required('Campo obrigatório').email('E-mail inválido'),
});

const StepResponsavel: React.FC<Props> = ({ onNext, onBack }) => {
  const { formData, updateResponsavel } = useRegister();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ResponsavelData>({
    resolver: yupResolver(schema),
    defaultValues: formData.responsavel,
  });

  const onSubmit = (data: ResponsavelData) => {
    updateResponsavel(data);
    saveLeadFromCadastro({
      name: data.nomeCompleto,
      email: data.email,
      phone: data.telefone,
      source: window.location.href,
    });
    onNext();
  };

  return (
    <form className="step-responsavel" onSubmit={handleSubmit(onSubmit)}>
      <h2 className="step-responsavel-title">Responsável</h2>
      <p className="step-responsavel-subtitle">Contato direto e rápido.</p>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="nomeCompleto" className="form-label">Nome Completo</label>
          <input
            id="nomeCompleto"
            className={`form-input ${errors.nomeCompleto ? 'error' : ''}`}
            placeholder="Nome completo"
            {...register('nomeCompleto')}
          />
          {errors.nomeCompleto && <span className="error-message">{errors.nomeCompleto.message}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="cpf" className="form-label">CPF</label>
          <Controller
            name="cpf"
            control={control}
            render={({ field }) => (
              <CpfMask
                {...field}
                className={`form-input ${errors.cpf ? 'error' : ''}`}
                placeholder="000.000.000-00"
              />
            )}
          />
          {errors.cpf && <span className="error-message">{errors.cpf.message}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="telefone" className="form-label">Telefone</label>
          <Controller
            name="telefone"
            control={control}
            render={({ field }) => (
              <PhoneMask
                {...field}
                className={`form-input ${errors.telefone ? 'error' : ''}`}
                placeholder="(00) 00000-0000"
              />
            )}
          />
          {errors.telefone && <span className="error-message">{errors.telefone.message}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group form-group-full">
          <label htmlFor="email" className="form-label">E-mail</label>
          <input
            id="email"
            type="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="email@exemplo.com"
            {...register('email')}
          />
          {errors.email && <span className="error-message">{errors.email.message}</span>}
        </div>
      </div>

      <div className="form-actions">
        <CustomButton
          type="submit"
          variant="contained"
          customBackground="#46558D"
          sx={{ width: '150px' }}
        >
          Continuar
        </CustomButton>
      </div>
    </form>
  );
};

export default StepResponsavel;
